'use client';

import NextImage from 'next/image';

import CTABackgroundGreyMobile from './assets/NoCatchesSectionBackground-mobile.png';
import CTABackgroundGreyTablet from './assets/NoCatchesSectionBackground-tablet.png';
import CTABackgroundGreyDesktop from './assets/NoCatchesSectionBackground-desktop.png';
import ArrowBlack from './assets/arrow-black.svg';
import { AppRatingLoader } from '../../../../components/AppRatingSection/components/AppRatingLoader';
import { AppStoreButtons } from '../../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { QRCode } from '../../../../components/QRCode/QRCode';
import { Rating } from '../../../../components/Rating/Rating';
import {
  RATING_ICON_WIDTH,
  RatingVariant,
} from '../../../../components/Rating/constants/constants';
import {
  AVERAGE_APP_RATING,
  IOS_APP_RATING,
  ANDROID_RATING_COUNT,
  IOS_ALL_RATING_COUNT,
} from '../../../../constants/constants';
import { useClientHasRendered } from '../../../../helpers/hooks';
import { useIsAndroid } from '../../../../helpers/hooks/useIsAndroid';
import { Source } from '../../../../components/NextImage/Source';
import { twMerge } from 'tailwind-merge';

interface IProps {
  className?: string;
}

export const NoCatchesSection = ({ className }: IProps) => {
  const isAndroid = useIsAndroid();
  const clientHasRendered = useClientHasRendered();

  return (
    <div className={twMerge('relative', className)}>
      {/* CTA Banner */}
      <div className="relative mx-8 max-w-(--breakpoint-lg) rounded-xl p-6 sm:mx-0 sm:p-8 lg:mx-auto xl:w-full">
        <picture>
          <Source media="(min-width: 990px)" src={CTABackgroundGreyDesktop}></Source>
          <Source media="(min-width: 600px)" src={CTABackgroundGreyTablet}></Source>
          <NextImage
            data-testid="appBannerLogo"
            alt="Fishbrain"
            src={CTABackgroundGreyMobile}
            fill
            className="rounded-xl"
          />
        </picture>
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row md:flex-col md:items-start">
          <div className="flex flex-row items-center sm:max-w-[50%] md:max-w-none md:gap-x-10">
            <div className="md:max-w-auto z-mid text-sea-foam flex flex-col gap-2 text-center sm:text-left">
              <Heading as="div" className="text-trench" variant="xs">
                Have you been fishing here?
              </Heading>
              <Text className="text-trench m-0" variant="sm">
                Log your catch and check out other catches from the community in the Fishbrain app.
              </Text>
            </div>
          </div>

          <div className="flex md:w-full md:flex-row md:justify-between">
            <div className="flex flex-col items-center justify-center gap-4 md:justify-start">
              <div className={'z-mid text-trench'}>
                <Rating
                  rating={isAndroid ? AVERAGE_APP_RATING : IOS_APP_RATING}
                  variant={RatingVariant.allStars}
                  textSize="sm"
                  isLarge
                  iconSize={RATING_ICON_WIDTH}
                  reviewPointColor={'text-trench'}
                />
                {clientHasRendered ? (
                  <Text variant="sm" as="span" className={`text-trench ml-1 font-semibold`}>
                    {isAndroid
                      ? `${ANDROID_RATING_COUNT} app store ratings`
                      : `${IOS_ALL_RATING_COUNT} ratings`}
                  </Text>
                ) : (
                  <AppRatingLoader key="NoCatches-apprating-key" />
                )}
              </div>

              <AppStoreButtons
                className="z-mid md:hidden"
                orientation="vertical"
                source="NoCatchesSection"
              />
            </div>

            <div className="z-mid relative ml-4 hidden items-center justify-center sm:justify-start md:flex">
              <div className="relative pb-2">
                <Text className={`text-trench m-0 -rotate-12 whitespace-pre-line`} variant="sm">
                  {`Scan the QR code 
                to download the app!`}
                </Text>
                <NextImage
                  alt="Arrow pointing to QR code"
                  src={ArrowBlack}
                  className="absolute right-0 bottom-0 translate-y-full"
                  unoptimized
                />
              </div>

              <QRCode className="bg-sea-foam rounded-md p-2 md:ml-4" source="NoCatchesSection" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
